import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_ROBOT_RETURN_LOCATION,
  DEPLOYMENT_RETURN_REASON_LABELS,
  DeploymentReturnReason,
  ROBOT_RETURN_LOCATION_CHOICES,
  ROBOT_RETURN_LOCATION_LABELS,
  RobotStatus,
  formatRobotShortId,
  type OwnerEntity,
  type RobotReturnLocation,
} from '@bhops/shared';
import { deploymentsApi } from '@/api/deployments';
import { robotsApi } from '@/api/robots';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Input, Select } from '@/components/Form';
import { useToast } from '@/components/Toast';
import { kstTodayYmd, ymdToIso } from '@/lib/format-date';
import {
  otherEntityRobotLabel,
  robotUsableAt,
  robotUseDeniedMessage,
} from '@/pages/robots/robot-entity';

// 계획서(2026-08-27) 요구 2 / 결정 2 — **원클릭 교체**.
//
// 종전엔 교체가 4단계 수동 합성이었고 순서를 틀리면 계약 연결이 끊겼다
// (고장 로봇을 먼저 회수 → 예약 해제 → 새 로봇이 계약 없이 나감).
// 이제 서버가 한 트랜잭션으로 처리하므로 이 창은 **무엇으로 바꿀지**만 묻는다.
//
// 새 로봇 후보를 좁히는 기준(계획서: "출고 가능(READY_TO_SHIP·미예약)만"):
//   · 상태 = 출고 대기(READY_TO_SHIP) — 아직 어디에도 나가지 않은 재고
//   · 서버가 다시 검사한다(배치 중·PHANTOM·쓸 수 없는 법인·폐기) — 화면은 편의일 뿐이고
//     경계는 언제나 서버다.
//   · 법인 = 교체 대상 배치에 **쓸 수 있는** 법인 — 오너 결정 2026-10-21(허니컴로보틱스 로봇도
//     비욘드허니컴 고객사에 배치, "#14와 #124-2도 고객사에 배치할 수 있게"). 10-20 에는 '새 로봇
//     법인 = 배치 법인'이라 같은 법인 로봇만 보였다. 이제 서버 교체(deployments.service swap)가
//     canUseRobotAt 으로 본다: 비욘드허니컴 배치 ← 비욘드허니컴·허니컴로보틱스 로봇,
//     허니컴로보틱스 배치 ← 허니컴로보틱스 로봇만(관리자도 같다). 콘솔은 그 거울
//     (robot-entity robotUsableAt)로 좁혀 '고를 수 있는데 저장이 실패하는' 목록을 만들지 않는다.
//   · 다른 법인 로봇에는 '(허니컴로보틱스)'를 붙인다 — 교체해도 소유 법인은 바뀌지 않으므로(회계
//     장부 그대로) 남의 장부 로봇을 내보낸다는 것을 고르는 사람이 알게.

interface Props {
  open: boolean;
  onClose: () => void;
  /** 교체 대상 배치(현재 나가 있는 로봇의 배치 기록). */
  deploymentId: string;
  /** 지금 나가 있는 로봇 — 창 머리에 "무엇을 바꾸는지" 적는다. */
  currentRobotLabel: string;
  /** 새로고침할 고객사(교체 후 카드가 즉시 반영되게). */
  customerId: string;
  /**
   * 교체 대상 배치의 법인(고객사 쪽) — 서버가 새 로봇을 이 법인에 쓸 수 있는지 본다
   * (오너 결정 2026-10-21: 비욘드허니컴 배치에는 허니컴로보틱스 로봇도 된다).
   */
  ownerEntity: OwnerEntity;
}

export function SwapRobotModal({
  open,
  onClose,
  deploymentId,
  currentRobotLabel,
  customerId,
  ownerEntity,
}: Props) {
  const toast = useToast();
  const qc = useQueryClient();

  const [newRobotId, setNewRobotId] = useState('');
  const [returnedAt, setReturnedAt] = useState<string>(() => kstTodayYmd());
  const [returnReason, setReturnReason] = useState<DeploymentReturnReason>(
    DeploymentReturnReason.SWAP_DEFECT,
  );
  const [returnLocation, setReturnLocation] = useState<RobotReturnLocation>(
    DEFAULT_ROBOT_RETURN_LOCATION,
  );
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setNewRobotId('');
    setReturnedAt(kstTodayYmd());
    setReturnReason(DeploymentReturnReason.SWAP_DEFECT);
    setReturnLocation(DEFAULT_ROBOT_RETURN_LOCATION);
    setNotes('');
    setError(null);
  }, [open]);

  const candidatesQ = useQuery({
    queryKey: ['robots', 'swap-candidates'],
    queryFn: () => robotsApi.list({ status: RobotStatus.READY_TO_SHIP, pageSize: 200 }),
    enabled: open,
    staleTime: 10_000,
  });

  const allReady = candidatesQ.data?.items ?? [];
  const candidates = allReady.filter((r) => robotUsableAt(r.ownerEntity, ownerEntity));
  // 출고 대기 로봇이 있는데 전부 이 배치에 쓸 수 없는 법인 것이면(허니컴로보틱스 배치 ←
  // 비욘드허니컴 로봇뿐) '없다'고만 말하면 거짓이 된다 — 왜 못 쓰는지를 서버와 같은 말로 말한다.
  const unusable = candidates.length === 0 ? allReady[0] : undefined;

  const swapM = useMutation({
    mutationFn: () =>
      deploymentsApi.swap(deploymentId, {
        newRobotId,
        returnedAt: ymdToIso(returnedAt),
        returnReason,
        returnLocation,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      }),
    onSuccess: (res) => {
      // 계약 승계 여부를 **말해 준다** — 조용히 넘어가면 운영자가 계약이 이어진 줄
      // 알고 넘어간다. 이어받을 계약이 없던 배치(옛 데이터)는 그 사실을 알려야 한다.
      toast.success(
        res.carriedContractId
          ? '교체했습니다. 계약이 새 로봇으로 이어졌습니다.'
          : '교체했습니다. 원래 배치에 예약 계약이 없어서 이어받을 계약은 없습니다.',
        '로봇 교체',
      );
      void qc.invalidateQueries({ queryKey: ['deployments'] });
      void qc.invalidateQueries({ queryKey: ['robots'] });
      void qc.invalidateQueries({ queryKey: ['customer', customerId] });
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!newRobotId) {
      setError('교체할 로봇을 골라 주세요.');
      return;
    }
    swapM.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="로봇 교체"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={swapM.isPending}>
            취소
          </Button>
          <Button type="submit" form="swap-robot-form" disabled={swapM.isPending}>
            {swapM.isPending ? '교체 중…' : '교체'}
          </Button>
        </>
      }
    >
      <form id="swap-robot-form" onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
        <div className="kuun-caption" data-testid="swap-current-robot">
          지금 나가 있는 로봇: <b>{currentRobotLabel}</b>
        </div>

        <Field label="교체할 로봇" htmlFor="swap-new-robot">
          <Select
            id="swap-new-robot"
            value={newRobotId}
            onChange={(e) => setNewRobotId(e.target.value)}
          >
            <option value="">선택하세요</option>
            {candidates.map((r) => (
              <option key={r.id} value={r.id}>
                {r.serialShort ? formatRobotShortId(r.serialShort) : (r.serialLong ?? r.id)} ·{' '}
                {r.model.displayName}
                {entitySuffix(r.ownerEntity, ownerEntity)}
              </option>
            ))}
          </Select>
          {/* 후보가 없을 때 빈 목록만 보여 주면 "왜 없는지"를 알 수 없다. */}
          {!candidatesQ.isLoading && candidates.length === 0 && (
            <div className="kuun-caption" data-testid="swap-no-candidates">
              {unusable
                ? `이 배치에 쓸 수 있는 출고 대기 로봇이 없습니다. ${robotUseDeniedMessage(unusable.ownerEntity, ownerEntity)}`
                : '출고 대기 상태의 로봇이 없습니다. 먼저 제조팀에서 출고 대기로 바꿔야 합니다.'}
            </div>
          )}
        </Field>

        <Field label="교체일" htmlFor="swap-date">
          <Input
            id="swap-date"
            type="date"
            value={returnedAt}
            onChange={(e) => setReturnedAt(e.target.value)}
          />
        </Field>

        <Field label="회수 사유" htmlFor="swap-reason">
          <Select
            id="swap-reason"
            value={returnReason}
            onChange={(e) => setReturnReason(e.target.value as DeploymentReturnReason)}
          >
            {Object.entries(DEPLOYMENT_RETURN_REASON_LABELS).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="회수한 로봇을 어디로" htmlFor="swap-location">
          <Select
            id="swap-location"
            value={returnLocation}
            onChange={(e) => setReturnLocation(e.target.value as RobotReturnLocation)}
          >
            {ROBOT_RETURN_LOCATION_CHOICES.map((v) => (
              <option key={v} value={v}>
                {ROBOT_RETURN_LOCATION_LABELS[v]}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="메모 (선택)" htmlFor="swap-notes">
          <Input
            id="swap-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="교체 사유나 특이사항"
          />
        </Field>

        {error && <FormError>{error}</FormError>}
      </form>
    </Modal>
  );
}

/** <option> 안에는 요소를 못 넣으므로 다른 법인 로봇의 법인 이름을 괄호로 덧붙인다(2026-10-21). */
function entitySuffix(robotEntity: OwnerEntity, deploymentEntity: OwnerEntity): string {
  const label = otherEntityRobotLabel(robotEntity, deploymentEntity);
  return label ? ` (${label})` : '';
}
